import {
  computed,
  reactive,
  toRefs,
  toValue,
  watchEffect,
  type ComputedRef,
  type MaybeRefOrGetter,
  type ToRefs
} from 'vue';
import { useStore } from 'vuex';
import { resolveCompanyDisplayFormats } from '@nova/core';

import {
  companyDisplayFormatEnabled,
  useCompanyDisplayFormatGate
} from '@/modules/company/companyDisplayFormatFlag';
import { toPlainCompany } from '@/modules/company/companyDisplaySettings';

import type { CarrierCompany } from '@/modules/company/types';

type CompanyArg = Pick<CarrierCompany, 'settings'> | null | undefined;
type ResolvedCompanyDisplayFormats = ReturnType<typeof resolveCompanyDisplayFormats>;

/** When the soft gate is off, pattern strings are undefined so legacy enum paths run. */
export type GatedCompanyDisplayFormats = Omit<
  ResolvedCompanyDisplayFormats,
  'dateFormatPattern' | 'timeFormatPattern' | 'dateTimeFormatPattern'
> & {
  dateFormatPattern: string | undefined;
  timeFormatPattern: string | undefined;
  dateTimeFormatPattern: string | undefined;
};

function gatedOffFormatPatterns(): GatedCompanyDisplayFormats {
  return {
    ...resolveCompanyDisplayFormats(null),
    dateFormatPattern: undefined,
    timeFormatPattern: undefined,
    dateTimeFormatPattern: undefined
  };
}

export type UseCompanySettingsReturn = {
  /** Soft-gated company (null when flag off/not ready). */
  gatedCompany: ComputedRef<ReturnType<typeof toPlainCompany>>;
  /** Resolved display format patterns; fields also exposed via toRefs below. */
  formatPatterns: GatedCompanyDisplayFormats;
} & ToRefs<GatedCompanyDisplayFormats>;

/**
 * Resolves carrier company display settings behind the soft gate.
 * Syncs Unleash into {@link companyDisplayFormatEnabled} (single source of truth) and
 * reads only that ref for gating — same gate as companyForDisplay / ExtendedAppointment.
 *
 * When the gate is off (or not ready), pattern strings are `undefined` so consumers
 * (e.g. useCustomFormsData) keep the pre–SUR-271 enum / warehouse-military path.
 *
 * @param company Optional company source. When omitted, uses `Auth/me.company`.
 *   Pass an explicit value/getter (including `null`) to override.
 */
export function useCompanySettings(
  company?: MaybeRefOrGetter<CompanyArg>
): UseCompanySettingsReturn {
  const store = useStore();
  useCompanyDisplayFormatGate();

  const gatedCompany = computed(() => {
    if (!companyDisplayFormatEnabled.value) {
      return null;
    }
    const source =
      company !== undefined
        ? (toValue(company) ?? null)
        : (store.getters['Auth/me']?.company ?? null);
    return toPlainCompany(source);
  });

  const formatPatterns = reactive(gatedOffFormatPatterns());

  watchEffect(() => {
    if (!companyDisplayFormatEnabled.value) {
      Object.assign(formatPatterns, gatedOffFormatPatterns());
      return;
    }
    Object.assign(formatPatterns, resolveCompanyDisplayFormats(gatedCompany.value));
  });

  return {
    gatedCompany,
    formatPatterns,
    ...toRefs(formatPatterns)
  };
}
